(() => {
  'use strict';

  const config = window.LunaraTheme || {};
  const routes = config.routes || {};

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (number === null || Number.isNaN(number)) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents || 0);
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return `${(value / 100).toFixed(2)}`;

    let amount;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        amount = formatWithDelimiters(value, 2);
    }
    return format.replace(placeholder[0], amount).replace(/<[^>]*>/g, '');
  }

  function announce(message) {
    const region = qs('#LunaraLiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 20);
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
      ...options
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || config.strings?.addError || 'Request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }

  class FocusDialog {
    constructor(shell, panel, closeSelector) {
      this.shell = shell;
      this.panel = panel;
      this.closeSelector = closeSelector;
      this.lastFocused = null;
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    open() {
      if (!this.shell || !this.panel) return;
      this.lastFocused = document.activeElement;
      this.shell.hidden = false;
      this.shell.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.handleKeydown);
      window.requestAnimationFrame(() => {
        this.shell.classList.add('is-open');
        this.panel.focus();
      });
    }

    close() {
      if (!this.shell) return;
      this.shell.classList.remove('is-open');
      this.shell.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.handleKeydown);
      window.setTimeout(() => {
        this.shell.hidden = true;
        if (!document.querySelector('.modal-shell.is-open, .cart-drawer-shell.is-open')) {
          document.body.classList.remove('overflow-hidden');
        }
      }, 220);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', this.panel)
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class CartDrawer {
    constructor() {
      this.shell = qs('[data-cart-drawer]');
      this.panel = this.shell ? qs('.cart-drawer', this.shell) : null;
      this.dialog = new FocusDialog(this.shell, this.panel, '[data-cart-close]');
      this.loading = false;
      this.bind();
    }

    bind() {
      document.addEventListener('click', (event) => {
        const open = event.target.closest('[data-cart-open]');
        if (open) {
          event.preventDefault();
          this.open();
          return;
        }
        const close = event.target.closest('[data-cart-close]');
        if (close && this.shell?.contains(close)) {
          event.preventDefault();
          this.dialog.close();
          return;
        }
        const remove = event.target.closest('[data-cart-remove]');
        if (remove && this.shell?.contains(remove)) {
          event.preventDefault();
          this.change(remove.dataset.cartRemove, 0);
          return;
        }
        const quantity = event.target.closest('[data-cart-quantity]');
        if (quantity && this.shell?.contains(quantity)) {
          event.preventDefault();
          this.change(quantity.dataset.cartQuantity, Number(quantity.dataset.quantityValue));
        }
      });
    }

    async open() {
      this.dialog.open();
      try {
        const cart = await this.fetchCart();
        this.render(cart);
      } catch (error) {
        console.error(error);
      }
    }

    async fetchCart() {
      return requestJSON(routes.cartJson || '/cart.js', { method: 'GET', headers: {} });
    }

    async change(key, quantity) {
      if (this.loading) return;
      this.loading = true;
      this.shell?.classList.add('is-loading');
      try {
        const cart = await requestJSON(routes.cartChange || '/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity })
        });
        this.render(cart);
      } catch (error) {
        console.error(error);
        announce(error.message);
      } finally {
        this.loading = false;
        this.shell?.classList.remove('is-loading');
      }
    }

    render(cart) {
      const count = Number(cart.item_count || 0);
      qsa('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.hidden = count === 0;
      });

      const countLabel = qs('[data-cart-drawer-count]', this.shell);
      if (countLabel) countLabel.textContent = `${count} ${document.documentElement.lang === 'pl' ? 'produktów' : 'items'}`;

      const body = qs('[data-cart-drawer-body]', this.shell);
      const footer = qs('[data-cart-footer]', this.shell);
      if (!body || !footer) return;

      if (!cart.items || cart.items.length === 0) {
        body.innerHTML = `
          <div class="cart-drawer__empty" data-cart-empty>
            <svg class="icon icon--large" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M6 8h12l1 13H5L6 8Z" stroke="currentColor" stroke-width="1.8"/><path d="M9 9V6a3 3 0 0 1 6 0v3" stroke="currentColor" stroke-width="1.8"/></svg>
            <h3>${escapeHTML(config.strings?.cartEmpty || 'Your cart is empty')}</h3>
          </div>`;
        footer.hidden = true;
        return;
      }

      body.innerHTML = `<div data-cart-items>${cart.items.map((item) => this.itemMarkup(item)).join('')}</div>`;
      footer.hidden = false;
      const total = qs('[data-cart-total]', footer);
      if (total) total.textContent = formatMoney(cart.total_price, config.moneyFormat);
    }

    itemMarkup(item) {
      const image = item.featured_image?.url || item.image || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHTML(item.variant_title)}</p>`
        : '';
      const original = item.original_line_price > item.final_line_price
        ? `<s>${formatMoney(item.original_line_price, config.moneyFormat)}</s>`
        : '';
      return `
        <article class="drawer-item" data-cart-item-key="${escapeHTML(item.key)}">
          <a class="drawer-item__image" href="${escapeHTML(item.url)}">${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(item.product_title)}" width="160" height="160" loading="lazy">` : ''}</a>
          <div class="drawer-item__content">
            <div class="drawer-item__top">
              <div><a href="${escapeHTML(item.url)}" class="drawer-item__title">${escapeHTML(item.product_title)}</a>${variant}</div>
              <button type="button" class="button-reset drawer-item__remove" aria-label="${escapeHTML(config.strings?.remove || 'Remove')}" data-cart-remove="${escapeHTML(item.key)}"><svg class="icon icon--small" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>
            </div>
            <div class="drawer-item__bottom">
              <div class="quantity-control">
                <button type="button" class="button-reset" aria-label="Decrease quantity" data-cart-quantity="${escapeHTML(item.key)}" data-quantity-value="${Math.max(0, item.quantity - 1)}"><svg class="icon icon--small" aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>
                <span>${item.quantity}</span>
                <button type="button" class="button-reset" aria-label="Increase quantity" data-cart-quantity="${escapeHTML(item.key)}" data-quantity-value="${item.quantity + 1}"><svg class="icon icon--small" aria-hidden="true" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>
              </div>
              <div class="drawer-item__prices">${original}<strong>${formatMoney(item.final_line_price, config.moneyFormat)}</strong></div>
            </div>
          </div>
        </article>`;
    }
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  class ProductSection {
    constructor(root, cartDrawer) {
      this.root = root;
      this.cartDrawer = cartDrawer;
      this.variants = this.parseVariants();
      this.variantControl = qs('[data-variant-select]', root);
      this.currentVariant = this.getCurrentVariant();
      this.modalShell = qs('[data-bundle-modal]', root);
      this.modalPanel = this.modalShell ? qs('.bundle-modal', this.modalShell) : null;
      this.modal = new FocusDialog(this.modalShell, this.modalPanel, '[data-bundle-close]');
      this.selectedQuantity = Number(qs('[data-bundle-option].is-selected', root)?.dataset.quantity || 1);
      this.bundleLabels = {
        save: root.dataset.bundleSaveLabel || 'You save',
        each: root.dataset.bundleEachLabel || 'each',
        total: root.dataset.bundleTotalLabel || 'Bundle total'
      };
      this.galleryIndex = qsa('[data-gallery-slide]', root).findIndex((slide) => !slide.hidden);
      if (this.galleryIndex < 0) this.galleryIndex = 0;
      this.bind();
      this.updateVariantUI();
      this.updateBundlePrices();
    }

    parseVariants() {
      const script = qs('[data-product-variants]', this.root);
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (error) { console.error(error); return []; }
    }

    getCurrentVariant() {
      const id = Number(this.variantControl?.value);
      return this.variants.find((variant) => Number(variant.id) === id) || this.variants[0] || null;
    }

    bind() {
      this.variantControl?.addEventListener('change', () => {
        this.currentVariant = this.getCurrentVariant();
        this.updateVariantUI();
        this.updateBundlePrices();
      });

      qs('[data-bundle-open]', this.root)?.addEventListener('click', () => {
        if (!this.currentVariant?.available) return;
        this.modal.open();
      });

      qsa('[data-bundle-close]', this.root).forEach((button) => button.addEventListener('click', () => this.modal.close()));

      qsa('[data-bundle-option]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          qsa('[data-bundle-option]', this.root).forEach((item) => item.classList.remove('is-selected'));
          button.classList.add('is-selected');
          this.selectedQuantity = Number(button.dataset.quantity || 1);
          this.updateBundleSummary();
        });
      });

      qs('[data-bundle-add]', this.root)?.addEventListener('click', () => this.addBundle());
      qs('[data-gallery-previous]', this.root)?.addEventListener('click', () => this.showGallery(this.galleryIndex - 1));
      qs('[data-gallery-next]', this.root)?.addEventListener('click', () => this.showGallery(this.galleryIndex + 1));
      qsa('[data-gallery-dot], [data-gallery-thumb]', this.root).forEach((button) => {
        button.addEventListener('click', () => this.showGallery(Number(button.dataset.galleryDot ?? button.dataset.galleryThumb)));
      });
    }

    updateVariantUI() {
      const variant = this.currentVariant;
      const price = qs('[data-product-price]', this.root);
      const compare = qs('[data-product-compare-price]', this.root);
      const buy = qs('[data-bundle-open]', this.root);
      const buyText = qs('[data-buy-button-text]', this.root);
      if (!variant) return;
      if (price) price.textContent = formatMoney(variant.price, config.moneyFormat);
      if (compare) {
        const show = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        compare.hidden = !show;
        compare.textContent = show ? formatMoney(variant.compare_at_price, config.moneyFormat) : '';
      }
      if (buy) buy.disabled = !variant.available;
      if (buyText && !variant.available) buyText.textContent = config.strings?.unavailable || 'Unavailable';
    }

    getBundlePricing(button) {
      const unitPrice = Number(this.currentVariant?.price || 0);
      const quantity = Math.max(1, Number(button?.dataset.quantity || 1));
      const rawDiscount = Number.parseFloat(button?.dataset.discountPercent || '0');
      const discountPercent = Number.isFinite(rawDiscount) ? Math.min(100, Math.max(0, rawDiscount)) : 0;
      const originalTotal = unitPrice * quantity;
      const discountedTotal = Math.max(0, Math.round(originalTotal * (1 - discountPercent / 100)));
      const savings = Math.max(0, originalTotal - discountedTotal);
      const discountedUnitPrice = Math.round(discountedTotal / quantity);
      return { quantity, discountPercent, originalTotal, discountedTotal, savings, discountedUnitPrice };
    }

    formatPercent(value) {
      return Number(value || 0).toLocaleString(document.documentElement.lang || undefined, {
        minimumFractionDigits: Number.isInteger(Number(value)) ? 0 : 0,
        maximumFractionDigits: 2
      });
    }

    updateBundlePrices() {
      qsa('[data-bundle-option]', this.root).forEach((button) => {
        const pricing = this.getBundlePricing(button);
        const compare = qs('[data-bundle-compare]', button);
        const price = qs('[data-bundle-price]', button);
        const saving = qs('[data-bundle-saving]', button);
        const unit = qs('[data-bundle-unit-price]', button);
        const hasDiscount = pricing.savings > 0;

        if (compare) {
          compare.hidden = !hasDiscount;
          compare.textContent = hasDiscount ? formatMoney(pricing.originalTotal, config.moneyFormat) : '';
        }
        if (price) price.textContent = formatMoney(pricing.discountedTotal, config.moneyFormat);
        if (saving) {
          saving.hidden = !hasDiscount;
          saving.textContent = hasDiscount
            ? `${this.bundleLabels.save} ${formatMoney(pricing.savings, config.moneyFormat)} (${this.formatPercent(pricing.discountPercent)}%)`
            : '';
        }
        if (unit) {
          unit.hidden = pricing.quantity <= 1;
          unit.textContent = pricing.quantity > 1
            ? `${formatMoney(pricing.discountedUnitPrice, config.moneyFormat)} / ${this.bundleLabels.each}`
            : '';
        }
      });
      this.updateBundleSummary();
    }

    updateBundleSummary() {
      const selected = qs('[data-bundle-option].is-selected', this.root) || qs('[data-bundle-option]', this.root);
      if (!selected) return;
      const pricing = this.getBundlePricing(selected);
      const total = qs('[data-bundle-selected-total]', this.root);
      const saving = qs('[data-bundle-selected-saving]', this.root);
      const addPrice = qs('[data-bundle-add-price]', this.root);
      if (total) total.textContent = formatMoney(pricing.discountedTotal, config.moneyFormat);
      if (saving) {
        saving.hidden = pricing.savings <= 0;
        saving.textContent = pricing.savings > 0
          ? `${this.bundleLabels.save} ${formatMoney(pricing.savings, config.moneyFormat)} (${this.formatPercent(pricing.discountPercent)}%)`
          : '';
      }
      if (addPrice) addPrice.textContent = `— ${formatMoney(pricing.discountedTotal, config.moneyFormat)}`;
    }

    async addBundle() {
      const button = qs('[data-bundle-add]', this.root);
      const errorBox = qs('[data-bundle-error]', this.root);
      if (!button || !this.currentVariant?.available) return;
      button.disabled = true;
      button.classList.add('is-loading');
      if (errorBox) errorBox.hidden = true;

      try {
        await requestJSON(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({
            items: [{ id: this.currentVariant.id, quantity: this.selectedQuantity }]
          })
        });
        const cart = await this.cartDrawer.fetchCart();
        this.cartDrawer.render(cart);
        this.modal.close();
        announce(config.strings?.added || 'Added to cart');
        window.setTimeout(() => this.cartDrawer.dialog.open(), 240);
      } catch (error) {
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
        announce(error.message);
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    showGallery(index) {
      const slides = qsa('[data-gallery-slide]', this.root);
      if (!slides.length) return;
      const normalized = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.hidden = i !== normalized;
        slide.classList.toggle('is-active', i === normalized);
      });
      qsa('[data-gallery-dot], [data-gallery-thumb]', this.root).forEach((control) => {
        const controlIndex = Number(control.dataset.galleryDot ?? control.dataset.galleryThumb);
        control.classList.toggle('is-active', controlIndex === normalized);
      });
      this.galleryIndex = normalized;
    }
  }

  function initHeader() {
    const toggle = qs('[data-mobile-menu-toggle]');
    const menu = qs('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      menu.hidden = open;
    });
  }

  function initDetails() {
    qsa('.faq-item, .product-details').forEach((details) => {
      details.addEventListener('toggle', () => {
        const marker = qs('summary span', details);
        if (marker) marker.textContent = details.open ? '−' : '+';
      });
    });
  }

  function init() {
    initHeader();
    initDetails();
    const cartDrawer = new CartDrawer();
    qsa('[data-product-section]').forEach((section) => new ProductSection(section, cartDrawer));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
